import type { RoleId } from "../role";

/**
 * S-NRI-ADAPT — بازنشستگیِ برگشت‌پذیرِ قابلیت‌های دانشگاهیِ بی‌جا در آموزش ضمن‌خدمت.
 *
 * قاعدهٔ حذف امن (خواستهٔ مالک): چیزی که «حذف» می‌شود پاک نمی‌شود — فقط نامش
 * اینجا می‌آید و در نقطهٔ مصرف (رندر ناوبری، چیپ لاگین، seed، CASL) فیلتر می‌شود،
 * پس از UI و RBAC کاملاً ناپدید است ولی کدش می‌ماند. برگشت = برداشتن نام از لیست.
 *
 * رجیستری‌های ثابت (SIDEBAR_BY_ROLE, ROLES, ROUTE_PATHS, ...) دست‌نخورده می‌مانند تا
 * تست مسدودکنندهٔ apps/web/tests/nav/nav-coherence.test.ts (که ثابت‌ها را می‌خواند)
 * سبز بماند؛ فیلترها فقط هنگام مصرف اعمال می‌شوند.
 *
 * سند: docs/NRI_MASTER_BLUEPRINT_FINAL.md بلوک ۷ + قرارداد S-NRI-ADAPT.
 */

/** نقش‌های مخصوص دانشگاهِ مدرک‌ده که در سازمان دولتی جایی ندارند. */
export const RETIRED_ROLES: readonly RoleId[] = [
  "parent", // والد/قیّم — آموزش بزرگ‌سالان، والدِ کارمند بی‌معناست
  "faculty_admin", // ریاست دانشکده — واحد آکادمیک؛ جایگزین: ادمین شرکت
  "ta", // دستیار آموزشی — مدرس تصحیح را پوشش می‌دهد
  "org", // سازمان B2B میراثی — جایگزین: نقش‌های شرکت + OrgWallet (S-NRI-6)
];

/** مسیرهای مخصوص مدل مدرک‌ده/شهریه‌ای که با ضمن‌خدمتِ شرکت‌پرداخت جور نیستند. */
export const RETIRED_MODULES: readonly string[] = [
  "network", // شبکهٔ اجتماعی — برای سامانهٔ دولتی؛ /community کافی است
  "add-drop", // حذف و اضافهٔ ترمی — مدل انتخاب‌واحد
  "my-path", // مسیر تحصیلی مرحله‌محور (پزشکی) — بی‌ربط به آب و برق
  "my-chart", // چارت درسی مدرک بر پایهٔ گرایش
  "tuition", // شهریه (و همهٔ فرزندانش: loans/refund/clearance) — شرکت‌پرداخت است
  "tuition/loans", // وام دانشجویی
  "tuition/refund", // استرداد شهریه
  "tuition/clearance", // تسویه‌حساب فراغت
  "financial-aid", // بورسیه/کمک‌هزینهٔ دانشگاهی

  // --- S-NRI-5 سطل الف: سلسله‌مراتبِ آکادمیکِ مدرک‌ده (جایگزین: درخت Organization
  //     در S-NRI-2). نگه‌داشتنِ هر دو = دو سلسله‌مراتبِ موازیِ گیج‌کننده. بازنشستهٔ
  //     دائم — این صفحات با معنای NRI بازنمی‌گردند (ساختار سازمانی جایشان را دارد).
  "admin/schools", // دانشکده‌ها
  "admin/faculties", // بخش‌های دانشکده
  "admin/departments", // گروه‌های آموزشی (زیرِ دانشکده)
  "admin/programs", // رشته‌ها
  "admin/specializations", // گرایش‌ها
  "admin/reference-data", // داده‌های مرجعِ آکادمیک (مقاطع/دانشکده…)
  "admin/ministry-library", // کتابخانهٔ وزارت علوم — وزارتِ اشتباه برای NRI

  // --- S-NRI-5 سطل الف: مقرراتِ مدرک‌ده و پذیرش (کارمندِ وزارت مشروط/اخراج/سنوات
  //     ندارد؛ پذیرش/کنکور بی‌معناست — نامزدسازیِ شرکت جایش را دارد S-NRI-6).
  "admin/degree-policies", // آستانه‌های مقطع (فراغت/اخراج)
  "admin/regulation", // آیین‌نامهٔ آموزشیِ مدرک‌ده
  "admin/commission", // کمیسیون موارد خاص (مشروطی/اخراج)
  "admin/stage-progress", // پیشرفت مرحله‌ای (پزشکی)
  "probation-review", // بازبینی مشروطی
  "admissions", // پذیرش
  "apply", // فرم درخواستِ پذیرش

  // --- S-NRI-5 سطل الف: صفحاتِ پروندهٔ دانشجوییِ مدرک‌ده (رشته/مقطع/معدل داخلشان).
  //     پیوندِ والد بی‌معناست؛ پروندهٔ فرد از admin/people در دسترس است.
  "admin/student-overview", // پروندهٔ دانشجو (رشته/مقطع/معدل)
  "admin/parent-links", // پیوند والد و دانشجو

  // --- شهریه: پیکربندیِ شهریه — NRI شرکت‌پرداخت است (OrgWallet، S-NRI-6).
  "admin/tuition-config", // پیکربندی شهریه

  // --- S-NRI-5B (بازسازی با معنای NRI، بستهٔ جدا): موقتاً از UI پنهان تا بازسازی.
  //     Students→فهرست کارمندان، Terms→بازه‌های برنامهٔ سالانه، curriculum→سرفصل‌ساز.
  //     کدشان دست‌نخورده؛ برگشت = برداشتن نام.
  //     «analytics-admin» در 5B.1 بازسازی و برگردانده شد (متریکِ ساعت/تکمیل/گواهی
  //     روی GET /v1/competency/org-rollup) — دیگر اینجا نیست.
  "admin/students", // → فهرست کارمندان/فراگیران (S-NRI-5B)
  "admin/instructors", // → فهرست مدرسان (S-NRI-5B)
  "admin/terms", // → بازه‌های برنامهٔ آموزشیِ سالانه (S-NRI-5B)
  "curriculum-builder", // → سرفصل‌سازِ دوره (S-NRI-5B)
];

/**
 * S-NRI-5 — «مقرراتِ آموزشِ مدرک‌ده»: مشروطی، آستانهٔ اخراج، کمیسیون موارد خاص،
 * سنوات مجاز، و نصاب معدل برای فراغت‌التحصیلی.
 *
 * اینها قابلیت نیستند، «آیین‌نامهٔ دانشگاه» هستند: کارمندِ وزارت نیرو به‌خاطر
 * معدلِ پایین «مشروط» یا «اخراج» نمی‌شود و «سنوات» ندارد — سقفِ او سقفِ ساعتِ
 * سالانه (۲۰۰) در لایهٔ شایستگی است (S-NRI-4). پس این بلوک‌ها از UI ناپدید
 * می‌شوند ولی کد و دادهٔ سمت سرور دست‌نخورده می‌ماند؛ برگشت = true.
 */
export const SHOW_ACADEMIC_REGULATION = false;

/**
 * S-NRI-5 سطل الف — سلسله‌مراتبِ آکادمیک (دانشکده→بخش→گروه→رشته→گرایش، AcademicHierarchyGuide).
 * جایگزینش درختِ Organization (S-NRI-2) است؛ راهنمای آکادمیک از هر داشبوردی که
 * میزبانش است ناپدید می‌شود ولی کامپوننت پاک نمی‌شود. برگشت = true.
 */
export const SHOW_ACADEMIC_HIERARCHY = false;

/**
 * S-NRI-5 سطل الف — متریک‌ها/فیلدهای مدرک‌دهِ فردی: رشته، مقطع، گرایش، معدل، و
 * «پیشرفت مسیر مدرک». کارمندِ وزارت این‌ها را ندارد؛ سنجهٔ او ساعت آموزش + گواهیِ
 * ساعت‌محور است (S-NRI-4). این تکه‌ها در پروندهٔ فرد پنهان می‌شوند، داده دست‌نخورده. برگشت = true.
 */
export const SHOW_DEGREE_METRICS = false;

/**
 * S-NRI-5 سطل الف — بخش‌های «شهریه»‌ایِ ادمین‌پنل (فهرست/مطالبات/صورتحساب شهریه).
 * NRI شرکت‌پرداخت است (OrgWallet، S-NRI-6) نه شهریهٔ فردی؛ این بخش‌ها پنهان می‌شوند
 * تا جریانِ «شرکت پرداخت می‌کند» جایگزینشان شود. کد/داده دست‌نخورده. برگشت = true.
 */
export const SHOW_TUITION_BILLING = false;

const asStrings = (xs: readonly unknown[]): readonly string[] => xs as readonly string[];

/** آیا این نقش بازنشسته است؟ */
export const isRetiredRole = (roleId?: string | null): boolean =>
  !!roleId && asStrings(RETIRED_ROLES).includes(roleId);

/**
 * آیا این مسیر بازنشسته است؟ id می‌تواند «network» یا «/tuition/loans» باشد؛
 * تطبیقِ دقیقِ segment یا فرزندِ مستقیم (پیشوند + "/") — نه تطبیق کاراکتری اشتباه
 * (مثلاً «my-chart» هرگز «my-courses» را نمی‌گیرد).
 */
export const isRetiredRoute = (routeId?: string | null): boolean => {
  if (!routeId) return false;
  const seg = routeId.replace(/^\//, "");
  return asStrings(RETIRED_MODULES).some((m) => seg === m || seg.startsWith(`${m}/`));
};
